// components/asset-management/assetModelContext.ts
// Builds a compact, text snapshot of the asset's live business-plan model for
// the Dealer agent's context (injected into the prompt's `situation`). This
// is what lets the agent SEE the current assumptions, metrics, rent roll and
// custom-tab formulas, so it can act on relative instructions ("make it more
// conservative", "re-let the largest vacant unit") and edit existing formulas
// by referencing the right cells. Pure function over inventory DTOs + the
// property's asset-model runtime slice (read from the Kernel by the caller).

import type { AssetModelRuntimeState, LeasingPropertyDTO, LeasingUnitDTO } from '#types/types';
import { keyToA1 } from '../tables/cellRef';
import {
  deriveAssetBaseline,
  deriveDefaultParams,
  deriveScenarioOutcomes,
  fmtEuroM as m,
  mergeRentRollEdits,
} from './scenario-engine';

/**
 * Compact snapshot of the focused asset's model, or null when there's nothing
 * useful to seed yet (units not hydrated). Bounded in size (top tenancies, a
 * cap on custom-tab cells) to stay token-cheap.
 */
export function buildAssetModelContext(
  property: LeasingPropertyDTO | null | undefined,
  units: ReadonlyArray<LeasingUnitDTO | undefined>,
  model: AssetModelRuntimeState,
): string | null {
  if (!property?.id) return null;
  const base0 = deriveAssetBaseline(property, units);
  if (!base0.hasFinancials || base0.totalAreaSqm <= 0) return null; // units not loaded yet

  const baseline = mergeRentRollEdits(base0, model.rentRollEdits ?? {});
  const params = model.params ?? deriveDefaultParams(baseline);
  const o = deriveScenarioOutcomes(baseline, params);
  const sheets = model.customSheets ?? [];
  const assetName = typeof property.name === 'string' && property.name.trim() ? property.name.trim() : 'Asset';

  const out: string[] = [];
  out.push(
    '=== ASSET BUSINESS-PLAN MODEL (live in the Asset tab). Edit ONLY via these tools: ' +
      'ASSET_MODEL_EDIT (assumptions), ASSET_MODEL_RENT_ROLL (tenancies), ' +
      'ASSET_MODEL_ADD_TAB / ASSET_MODEL_WRITE_CELLS / ASSET_MODEL_REMOVE_TAB (custom tabs). ' +
      'Prefer LIVE FORMULAS (cells starting with "=") over hardcoded numbers. ===',
  );
  out.push(
    `Asset: ${assetName} | GLA ${baseline.totalAreaSqm.toLocaleString()} m² | occupancy ${baseline.currentOccupancyPct}% | WAULT ${baseline.waultYears}y | reversion +${baseline.reversionPct}%`,
  );
  out.push(
    `Assumptions (current values, all editable): capexEnvelope ${m(params.capexEnvelope)}, rentUpliftPct ${params.rentUpliftPct}, timelineMonths ${params.timelineMonths}, tenantMix ${params.tenantMix}, sustainability ${params.sustainability}, holdYears ${params.holdYears}, rentGrowthPct ${params.rentGrowthPct}, opexRatioPct ${params.opexRatioPct}, exitCapRatePct ${params.exitCapRatePct}, acqCostPct ${params.acqCostPct}, sellingCostPct ${params.sellingCostPct}, ltvPct ${params.ltvPct}, interestRatePct ${params.interestRatePct}, arrangementFeePct ${params.arrangementFeePct}, mezzLtvPct ${params.mezzLtvPct}, mezzRatePct ${params.mezzRatePct}, prefRatePct ${params.prefRatePct}, promotePct ${params.promotePct}`,
  );
  out.push(
    `Today: NOI ${m(baseline.currentNoi)}, value ${m(baseline.currentValue)}, passing rent ${m(baseline.passingRentRoll)}, ERV ${m(baseline.ervAnnual)}`,
  );
  out.push(
    `Returns: unlevered IRR ${o.irrPct}%, levered IRR ${o.leveredIrrPct}%, equity multiple ${o.equityMultiple}x, profit ${m(o.profitEur)}, profit-on-cost ${o.profitOnCostPct}%, yield-on-cost ${o.yieldOnCostPct}%, LP net IRR ${o.waterfall.lpIrrPct}%, total cost ${m(o.totalProjectCost)}, equity ${m(o.equity)}, debt ${m(o.peakDebt)}, exit GDV ${m(o.exitValue)}, stabilised ICR ${o.minIcr}x`,
  );

  const occ = baseline.rentRoll.filter((l) => l.isOccupied).length;
  const vac = baseline.rentRoll.length - occ;
  const top = baseline.rentRoll
    .slice(0, 8)
    .map(
      (l) =>
        `${l.label}(${l.areaSqm}m²,${l.isOccupied ? `${m(l.passingRent)},exp ${l.leaseExpiryYear}y` : 'vacant'})`,
    )
    .join('; ');
  out.push(
    `Rent roll: ${baseline.rentRoll.length} tenancies (${occ} let, ${vac} vacant). Reference a tenancy by its unit label in ASSET_MODEL_RENT_ROLL. Largest by area: ${top}`,
  );
  // Explicit vacant list (already area-sorted) so "re-let the largest vacant" resolves correctly.
  const vacantTop = baseline.rentRoll
    .filter((l) => !l.isOccupied)
    .slice(0, 8)
    .map((l) => `${l.label}(${l.areaSqm}m²)`)
    .join('; ');
  if (vacantTop) {
    out.push(`Vacant units, largest first (the ONLY re-let candidates): ${vacantTop}`);
  }

  if (sheets.length > 0) {
    out.push(`Custom tabs (${sheets.length}), cells shown as A1=value/formula so you can edit them:`);
    for (const sh of sheets) {
      const cellStr = Object.entries(sh.cells)
        .slice(0, 40)
        .map(([k, v]) => `${keyToA1(k)}=${v}`)
        .join(', ');
      out.push(`  "${sh.name}": ${cellStr || '(empty)'}`);
    }
  }

  return out.join('\n');
}
